import React, { useState } from 'react';
import ToolPage from '@/components/ToolPage';
import { Field, inputClass } from '@/components/calcUi';
import { getToolBySlug } from '@/lib/tools';
import { formatNumber } from '@/lib/currency';

const num = (v) => (v === '' || isNaN(Number(v)) ? NaN : Number(v));

function category(bmi) {
  if (isNaN(bmi)) return { label: '—', color: 'text-slate-400' };
  if (bmi < 18.5) return { label: 'Underweight', color: 'text-sky-600' };
  if (bmi < 25) return { label: 'Healthy weight', color: 'text-emerald-600' };
  if (bmi < 30) return { label: 'Overweight', color: 'text-amber-600' };
  return { label: 'Obesity', color: 'text-rose-600' };
}

export function BMICalculator() {
  const tool = getToolBySlug('bmi-calculator');
  const [system, setSystem] = useState('metric');
  const [cm, setCm] = useState('175');
  const [kg, setKg] = useState('70');
  const [ft, setFt] = useState('5');
  const [inch, setInch] = useState('9');
  const [lb, setLb] = useState('154');

  let bmi = NaN;
  if (system === 'metric') {
    const h = num(cm) / 100, w = num(kg);
    if (!isNaN(h) && !isNaN(w) && h > 0) bmi = w / (h * h);
  } else {
    const totalIn = num(ft) * 12 + num(inch);
    const w = num(lb);
    if (!isNaN(totalIn) && !isNaN(w) && totalIn > 0) bmi = (703 * w) / (totalIn * totalIn);
  }
  const cat = category(bmi);
  return (
    <ToolPage
      tool={tool}
      intro={<p>Body Mass Index is a quick screening estimate of whether a person's weight is in a healthy range for their height. It is not a diagnosis.</p>}
      formula={<>
        <div>Metric:  BMI = kg / m²</div>
        <div>Imperial:  BMI = (lb × 703) / in²</div>
      </>}
      examples={[
        'A person 175 cm tall weighing 70 kg has a BMI of about 22.9 (healthy).',
        'A person 5\'9" (175 cm) weighing 200 lb has a BMI of about 29.5 (overweight).',
      ]}
      faqs={[
        { q: 'Is BMI reliable?', a: 'BMI is a fast screening tool for adults, but it does not distinguish muscle from fat and may misclassify very muscular or very short/tall individuals.' },
        { q: 'Does BMI apply to children?', a: 'No — children and teens use percentile-based BMI-for-age charts. Use a pediatric tool for anyone under 18.' },
      ]}
      limitations={<>BMI is a general screening tool for adults 18+. It does not diagnose health, and it does not account for muscle mass, bone density, ethnicity, or age. Talk to a qualified healthcare professional before making decisions.</>}
    >
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="flex gap-2">
            {['metric','imperial'].map((k) => (
              <button key={k} type="button" onClick={() => setSystem(k)} data-testid={`bmi-system-${k}`}
                className={`flex-1 h-11 rounded-lg text-sm font-medium capitalize ${system === k ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700'}`}>{k}</button>
            ))}
          </div>
          {system === 'metric' ? (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Height (cm)" htmlFor="bmi-cm"><input id="bmi-cm" data-testid="bmi-cm" type="number" step="any" value={cm} onChange={(e) => setCm(e.target.value)} className={inputClass} /></Field>
              <Field label="Weight (kg)" htmlFor="bmi-kg"><input id="bmi-kg" data-testid="bmi-kg" type="number" step="any" value={kg} onChange={(e) => setKg(e.target.value)} className={inputClass} /></Field>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4">
              <Field label="Feet" htmlFor="bmi-ft"><input id="bmi-ft" data-testid="bmi-ft" type="number" value={ft} onChange={(e) => setFt(e.target.value)} className={inputClass} /></Field>
              <Field label="Inches" htmlFor="bmi-in"><input id="bmi-in" data-testid="bmi-in" type="number" value={inch} onChange={(e) => setInch(e.target.value)} className={inputClass} /></Field>
              <Field label="Weight (lb)" htmlFor="bmi-lb"><input id="bmi-lb" data-testid="bmi-lb" type="number" value={lb} onChange={(e) => setLb(e.target.value)} className={inputClass} /></Field>
            </div>
          )}
        </div>
        <div className="result-panel rounded-xl p-6">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">BMI</div>
          <div className="text-5xl font-bold text-emerald-700" data-testid="bmi-result">
            {isNaN(bmi) ? '—' : formatNumber(bmi, 1)}
          </div>
          <div className={`mt-2 text-lg font-semibold ${cat.color}`} data-testid="bmi-category">{cat.label}</div>
          <div className="mt-4 text-xs text-slate-500 leading-relaxed">Adult reference ranges (WHO): under 18.5 underweight, 18.5–24.9 healthy, 25–29.9 overweight, 30+ obesity.</div>
        </div>
      </div>
    </ToolPage>
  );
}
